/**
 * @module AssertionDisplay
 * @purpose Single owner of the assertion→tier and assertion→label mapping for
 *   ResolvedObservation.presenceAssertion / executionAssertion. Replaces the two
 *   byte-identical if-chains previously duplicated in use-canvas-state.ts and
 *   ReasoningCommentGroup.tsx, which silently rendered every unrecognised
 *   assertion value as "unknown" — collapsing "this artefact says nothing about
 *   files" into "we do not know whether a file was there".
 * @depends types/reasoning.ts (PresenceAssertion, ExecutionAssertion)
 * @dependedOnBy use-canvas-state.ts, ReasoningCommentGroup.tsx, ReasoningPathLadder.tsx
 *
 * @rules
 * - Performance: pure functions, no side effects, no React imports
 * - State: stateless
 * - Errors: n/a — inputs are typed; undefined treated as 'unknown'
 */

import type { ParseKeys } from 'i18next'
import type { ExecutionAssertion, PresenceAssertion } from '../types/reasoning'

/**
 * The literal key union i18next generates for the `analysis` namespace
 * (see i18n/types.ts). Typing the label Records against this — rather than
 * plain `string` — means a typo'd or renamed JSON key fails `npm run
 * typecheck` here too, not just at the `t()` call site.
 */
type AnalysisTranslationKey = ParseKeys<'analysis'>

/**
 * Representative-selection tiers. NOT a strength-of-evidence scale — same status
 * as `library_order` at the database layer and `execution_assertion_rank` at
 * Stage 6. Tiers order a list of observations and pick a spokesperson for an
 * entity; nothing may read a lower number as weaker evidence.
 *
 * Order, low to high: presence axis states that assert something (0-1), then
 * execution axis states that assert something (2-3), then either axis stating
 * "not applicable" (4), then "unknown" last (5) — the only value that asserts
 * nothing.
 */
export const CAUSAL_TIER_PRESENCE_RECORDED = 0
export const CAUSAL_TIER_PRESENCE_REFERENCED = 1
export const CAUSAL_TIER_EXECUTION_RECORDED_OR_SUPPORTED = 2
export const CAUSAL_TIER_EXECUTION_EVALUATED = 3
export const CAUSAL_TIER_NOT_APPLICABLE = 4
export const CAUSAL_TIER_UNKNOWN = 5

const DEFAULT_ASSERTION = 'unknown'

/**
 * Presence axis: definite tier, or null to defer to the execution axis.
 *
 * Exhaustive over PresenceAssertion — adding a value to that union without
 * adding a key here fails `npm run typecheck` (AC14). This is the guarantee
 * an if-chain cannot provide.
 */
const PRESENCE_TIER: Record<PresenceAssertion, number | null> = {
  recorded: CAUSAL_TIER_PRESENCE_RECORDED,
  referenced: CAUSAL_TIER_PRESENCE_REFERENCED,
  not_applicable: null,
  unknown: null,
}

/**
 * Execution axis: definite tier, or null to defer further (to the shared
 * not_applicable / unknown fallback below).
 *
 * Exhaustive over ExecutionAssertion — same compile-time guarantee as
 * PRESENCE_TIER.
 */
const EXECUTION_TIER: Record<ExecutionAssertion, number | null> = {
  recorded: CAUSAL_TIER_EXECUTION_RECORDED_OR_SUPPORTED,
  supported: CAUSAL_TIER_EXECUTION_RECORDED_OR_SUPPORTED,
  evaluated: CAUSAL_TIER_EXECUTION_EVALUATED,
  not_applicable: null,
  unknown: null,
}

/**
 * i18n label key per presence value, under the `analysis` namespace.
 * Exhaustive over PresenceAssertion for the same reason as PRESENCE_TIER.
 */
const PRESENCE_LABEL_KEY: Record<PresenceAssertion, AnalysisTranslationKey> = {
  recorded: 'reasoning.assertion.presence.recorded',
  referenced: 'reasoning.assertion.presence.referenced',
  not_applicable: 'reasoning.assertion.presence.notApplicable',
  unknown: 'reasoning.assertion.presence.unknown',
}

/**
 * i18n label key per execution value, under the `analysis` namespace.
 * Exhaustive over ExecutionAssertion for the same reason as EXECUTION_TIER.
 */
const EXECUTION_LABEL_KEY: Record<ExecutionAssertion, AnalysisTranslationKey> = {
  recorded: 'reasoning.assertion.execution.recorded',
  supported: 'reasoning.assertion.execution.supported',
  evaluated: 'reasoning.assertion.execution.evaluated',
  not_applicable: 'reasoning.assertion.execution.notApplicable',
  unknown: 'reasoning.assertion.execution.unknown',
}

interface AssertedObservation {
  presenceAssertion?: PresenceAssertion
  executionAssertion?: ExecutionAssertion
}

function hasNotApplicable(obs: AssertedObservation): boolean {
  return obs.presenceAssertion === 'not_applicable' || obs.executionAssertion === 'not_applicable'
}

/**
 * Representative-selection tier for one observation.
 *
 * @behaviour Tests the presence axis first, then the execution axis, matching
 *   the original if-chain's precedence exactly: an observation asserting
 *   `presence: not_applicable` and `execution: recorded` (the event-4688
 *   shape) still resolves to CAUSAL_TIER_EXECUTION_RECORDED_OR_SUPPORTED, not
 *   CAUSAL_TIER_NOT_APPLICABLE. The two per-axis Records are consulted in
 *   sequence — they are never flattened into a single two-axis table.
 * @errors n/a — undefined assertions default to 'unknown'.
 */
export function causalTier(obs: AssertedObservation): number {
  const presenceTier = PRESENCE_TIER[obs.presenceAssertion ?? DEFAULT_ASSERTION]
  if (presenceTier !== null) return presenceTier

  const executionTier = EXECUTION_TIER[obs.executionAssertion ?? DEFAULT_ASSERTION]
  if (executionTier !== null) return executionTier

  if (hasNotApplicable(obs)) return CAUSAL_TIER_NOT_APPLICABLE
  return CAUSAL_TIER_UNKNOWN
}

/**
 * i18n label key for a presenceAssertion value. Pass to `t()` — this module
 * does not call useTranslation itself (it has no React dependency).
 *
 * @errors n/a — undefined defaults to the 'unknown' label key.
 */
export function presenceAssertionLabelKey(value: PresenceAssertion | undefined): AnalysisTranslationKey {
  return PRESENCE_LABEL_KEY[value ?? DEFAULT_ASSERTION]
}

/**
 * i18n label key for an executionAssertion value. Pass to `t()` — this module
 * does not call useTranslation itself (it has no React dependency).
 *
 * @errors n/a — undefined defaults to the 'unknown' label key.
 */
export function executionAssertionLabelKey(value: ExecutionAssertion | undefined): AnalysisTranslationKey {
  return EXECUTION_LABEL_KEY[value ?? DEFAULT_ASSERTION]
}
